import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Spin } from "antd";
import { fetchMeta, fetchDashboard } from "./api";
import { getLabels, periodLabel } from "./labels";
import { DynamicsBar, ReportBar } from "./components/Toolbars";
import SummaryPanel from "./components/SummaryPanel";
import IndicatorCard from "./components/IndicatorCard";
import PlanFactChart from "./components/PlanFactChart";
import SummaryTable from "./components/SummaryTable";
import "./HealthCare.css";

export const HealthCare = () => {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "kz").startsWith("ru") ? "ru" : "kz";
  const L = getLabels(lang);

  const [meta, setMeta] = useState(null);
  const [state, setState] = useState({
    type: null,
    report: null,
    from: null,
    to: null,
  });
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchMeta()
      .then((m) => {
        setMeta(m);
        const type = m.default_type || "year";
        const list = m.periods[type] || [];
        setState({
          type,
          report: list.length ? list[list.length - 1].key : null, // свежий
          from: list.length ? list[0].key : null, // ранний
          to: list.length ? list[list.length - 1].key : null, // свежий
        });
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    const { type, report, from, to } = state;
    if (!type || !report || !from || !to) return;
    setLoading(true);
    fetchDashboard({ type, report, from, to })
      .then((d) => {
        setDash(d);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, [state]);

  const periods = useMemo(
    () => (meta && state.type ? meta.periods[state.type] || [] : []),
    [meta, state.type]
  );

  const handleType = useCallback(
    (type) => {
      if (!meta || type === state.type) return;
      const list = meta.periods[type] || [];
      setState({
        type,
        report: list.length ? list[list.length - 1].key : null,
        from: list.length ? list[0].key : null,
        to: list.length ? list[list.length - 1].key : null,
      });
    },
    [meta, state.type]
  );

  const handleRange = useCallback(
    (from, to) => setState((s) => ({ ...s, from, to })),
    []
  );
  const handleReport = useCallback(
    (report) => setState((s) => ({ ...s, report })),
    []
  );

  const pLabel = (key) => {
    const p = periods.find((x) => x.key === key);
    return p ? periodLabel(p, lang) : key || "";
  };

  if (error) {
    return (
      <div className="hc-page">
        <div className="hc-state">{L.loadError}</div>
      </div>
    );
  }

  if (!dash) {
    return (
      <div className="hc-page">
        <div className="hc-state">
          <Spin size="large" />
        </div>
      </div>
    );
  }

  const { summary, items } = dash;
  const hasItems = items && items.length > 0;

  return (
    <div className="hc-page">
      <div className="hc-header">
        <DynamicsBar
          type={state.type}
          periods={periods}
          from={state.from}
          to={state.to}
          onType={handleType}
          onRange={handleRange}
          lang={lang}
          L={L}
        />
      </div>

      <div className="hc-legend">
        <div className="hc-legend__grid">
          <span className="hc-legend__item">
            <span className="hc-legend__dot hc-legend__dot--good" />
            {L.legendDone}
          </span>
          <span className="hc-legend__item">
            <span className="hc-legend__dot hc-legend__dot--bad" />
            {L.legendNotDone}
          </span>
          <span className="hc-legend__item">{L.legendArrow}</span>
          <span className="hc-legend__item">
            <span className="hc-legend__line hc-legend__line--plan" />
            {L.legendPlan}
          </span>
          <span className="hc-legend__item">
            <span className="hc-legend__line hc-legend__line--fact" />
            {L.legendFact}
          </span>
          {state.type === "quarter" && (
            <div className="hc-legend__item hc-legend__item--note">
              {L.legendQuarterNote}
            </div>
          )}{" "}
        </div>
      </div>

      <Spin spinning={loading}>
        <section className="hc-panel">
          <header className="hc-panel__header">
            <span className="hc-panel__title">{L.statPanel}</span>
            <span className="hc-panel__subtitle">
              · {L.forPeriod}: {pLabel(state.report)}
            </span>
          </header>
          <SummaryPanel summary={summary} L={L} />
        </section>

        <section className="hc-panel">
          <header className="hc-panel__header">
            <span className="hc-panel__title">{L.indicatorsTitle}</span>
            <span className="hc-panel__subtitle">
              · {L.bigNumberIs} — {pLabel(state.report)} · {L.chartIs}:{" "}
              {pLabel(state.from)} — {pLabel(state.to)}
            </span>
          </header>
          {!hasItems ? (
            <div className="hc-state">{L.noDataPeriod}</div>
          ) : (
            <div className="hc-grid">
              {items.map((item) => (
                <IndicatorCard
                  key={item.code}
                  item={item}
                  reportKey={state.report}
                  lang={lang}
                  L={L}
                />
              ))}
            </div>
          )}
        </section>

        <ReportBar
          type={state.type}
          periods={periods}
          report={state.report}
          onType={handleType}
          onReport={handleReport}
          lang={lang}
          L={L}
        />

        <section className="hc-panel">
          <header className="hc-panel__header">
            <span className="hc-panel__title">{L.planFactTitle}</span>
            <span className="hc-panel__subtitle">
              · {L.forPeriod}: {pLabel(state.report)}
            </span>
          </header>
          {!hasItems ? (
            <div className="hc-state">{L.noDataPeriod}</div>
          ) : (
            <PlanFactChart items={items} lang={lang} L={L} />
          )}
        </section>

        <section className="hc-panel">
          <header className="hc-panel__header">
            <span className="hc-panel__title">{L.tableTitle}</span>
            <span className="hc-panel__subtitle">
              · {L.forPeriod}: {pLabel(state.report)}
            </span>
          </header>
          {!hasItems ? (
            <div className="hc-state">{L.noDataPeriod}</div>
          ) : (
            <SummaryTable items={items} lang={lang} L={L} />
          )}
        </section>
      </Spin>
    </div>
  );
};

export default HealthCare;
